import { useState, useMemo, useEffect, useCallback } from 'react'

import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import {
  ContentStates,
  type ContentStateEnum,
} from '../../enums/ContentStateEnum'

interface INavigableItem {
  uuid: string
}

interface IUseGlobalSearchKeyboardNavigationInput {
  query: string
  recentItems: GlobalSearchRecentItem[]
  filteredResultItems: GlobalSearchResultItem[]
  contentState: Nullable<ContentStateEnum>
  openRecentItem: (item: GlobalSearchRecentItem) => void
  openResultItem: (item: GlobalSearchResultItem) => void
}

interface INavigation {
  navigateDown: () => void
  navigateUp: () => void
  openHighlightedItem: () => boolean
}

interface IUseGlobalSearchKeyboardNavigationResult extends INavigation {
  highlightedIndex: Nullable<number>
  isExpanded: boolean
  activeDescendantId: string | undefined
  resetHighlight: () => void
}

export const useGlobalSearchKeyboardNavigation = ({
  query,
  recentItems,
  filteredResultItems,
  contentState,
  openRecentItem,
  openResultItem,
}: IUseGlobalSearchKeyboardNavigationInput): IUseGlobalSearchKeyboardNavigationResult => {
  const [highlightedIndex, setHighlightedIndex] =
    useState<Nullable<number>>(null)

  const navigableItems = useMemo<readonly INavigableItem[]>(() => {
    if (contentState === ContentStates.RECENTS) {
      return recentItems.map((item) => ({ uuid: item.uuid }))
    }
    if (contentState === ContentStates.RESULTS) {
      return filteredResultItems.map((item) => ({ uuid: item.uuid }))
    }
    return []
  }, [contentState, recentItems, filteredResultItems])

  useEffect(() => {
    setHighlightedIndex(null)
  }, [query])

  useEffect(() => {
    setHighlightedIndex((current) => {
      if (current !== null && current >= navigableItems.length) return null
      return current
    })
  }, [navigableItems.length])

  const navigateDown = useCallback(() => {
    if (navigableItems.length === 0) return
    setHighlightedIndex((current) => {
      if (current === null) return 0
      return Math.min(current + 1, navigableItems.length - 1)
    })
  }, [navigableItems.length])

  const navigateUp = useCallback(() => {
    if (navigableItems.length === 0) return
    setHighlightedIndex((current) => {
      if (current === null) return null
      if (current === 0) return null
      return current - 1
    })
  }, [navigableItems.length])

  const openHighlightedItem = useCallback((): boolean => {
    if (highlightedIndex === null) return false
    if (contentState === ContentStates.RECENTS) {
      const item = recentItems[highlightedIndex]
      if (item === undefined) return false
      openRecentItem(item)
      return true
    }
    if (contentState === ContentStates.RESULTS) {
      const item = filteredResultItems[highlightedIndex]
      if (item === undefined) return false
      openResultItem(item)
      return true
    }
    return false
  }, [
    highlightedIndex,
    contentState,
    recentItems,
    filteredResultItems,
    openRecentItem,
    openResultItem,
  ])

  const resetHighlight = useCallback(() => {
    setHighlightedIndex(null)
  }, [])

  const isExpanded =
    contentState === ContentStates.RECENTS ||
    contentState === ContentStates.RESULTS

  const activeDescendantId =
    highlightedIndex !== null
      ? (navigableItems[highlightedIndex]?.uuid ?? undefined)
      : undefined

  return {
    highlightedIndex,
    isExpanded,
    activeDescendantId,
    navigateDown,
    navigateUp,
    openHighlightedItem,
    resetHighlight,
  }
}
